import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { deleteStream, updateStream, updateStreamProperty } from "../../redux/actions/streams";
import AnalyticsCard from "./analyticsCard";
import { StreamDescription } from "./types";
import _ from "lodash";
import cx from "classnames";
import styles from "./streamDescription.module.scss";
import Button from "../../components/button";
import DeleteStream from "./deleteStream";
import { useSelector } from "react-redux";
import RichTextEditor from "src/components/richTextEditor";
import { EditBanner } from "./editBanner";
import Thumbnail from "src/components/thumbnail";
import { Banner } from "src/types/common";
import { MediaFile } from "src/types/media";
import { uploadMedia } from "src/redux/actions/media";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { useNavigate } from "react-router-dom";
import { ReadMoreHtml } from "src/components/readMore";
import { useStreamsAdoption } from "src/redux/actions/adoption/streams";
import Tooltip from "src/components/tooltip";


const StreamDescriptionView: React.FC<{ id: string, handleExpandContainer: (isExpanded: boolean) => void; }> = ({ id, handleExpandContainer }) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    trackEditStreamClicked, trackEditStreamConfirmClicked, trackChangeBackgroundClicked, trackDeleteStreamClicked, 
    trackDeleteStreamConfirmClicked, trackStreamEdited, trackStreamDeleted, trackStreamEditViewed
   } = useStreamsAdoption();

  const loadingStream = useSelector((state: any) => _.get(state, `stream.items.[${id}].loading`));
  const stream = useSelector((state: any) => _.get(state, `stream.items[${id}].item`, null));
  const analytics = useSelector((state: any) => _.get(state, `stream.items[${id}].item.analytics`, null));
  const updating = useSelector((state: any) => _.get(state, `stream.items[${id}].updation.updating`, false));
  const updated = useSelector((state: any) => _.get(state, `stream.items[${id}].updation.updated`, false));

  const uploaded = useSelector((state: any) => {
    const background = _.get(state, `stream.items[${id}].edit.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loaded`, false);
    }

    return false;
  });

  const uploadedBackground = useSelector((state: any) => {
    const background = _.get(state, `stream.items[${id}].edit.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].item`);
    }

    return false;
  });

  const uploading = useSelector((state: any) => {
    const background = _.get(state, `stream.items[${id}].edit.banner.background`);
    const entityType = _.get(background, 'entityType');
    if (entityType === 'media-preview') {
      return _.get(state, `media['${background.id}'].loading`, false);
    }

    return false;
  })

  const deleting = useSelector((state: any) => _.get(state, `stream.items[${id}].deletion.deleting`, false));
  const deleted = useSelector((state: any) => _.get(state, `stream.items[${id}].deletion.deleted`, false));
  const error = useSelector((state: any) => _.get(state, `stream.items[${id}].deletion.error`, null));


  const deletStreamProps: IUseFetchProps = {
    action: deleteStream,
    loading: deleting,
    succeeded: deleted,
    response: [],
    error: error
  }

  const { handleFetch: deleteStreamRequest } = useFetch(deletStreamProps);

  const updateStreamProps: IUseFetchProps = {
    action: updateStream,
    loading: updating,
    succeeded: updated,
    response: stream,
    error: null
  };

  const { handleFetch: updateStreamRequest } = useFetch(updateStreamProps);

  const [description, setDescription] = useState<StreamDescription>();
  const [isExpanded, setIsExpanded] = useState(true);
  const [open, setOpen] = useState(false);
  const [isEditStream, setIsEditStream] = useState(false);
  const [isEditBanner, setIsEditBanner] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(_.get(stream, "banner.background"));
  const [cropDetails, setCropDetails] = useState(
    _.get(stream, "banner.background")
  );
  const [updatedThumbnail, setUpdatedThumbnail] = useState<Banner | null>(null);
  const [content, setContent] = useState("");
  const [position, setPosition] = useState("center center");

  useEffect(() => {
    if (_.get(analytics, "item")) {
      setDescription({
        id: analytics.item.id,
        name: stream.name,
        description: stream.summary,
        banner: stream.banner,
        totalViews: analytics.item.totalViews,
        totalDownloads: analytics.item.totalDownloads
      });
    }
  }, [analytics])

  useEffect(() => {
    if(_.get(stream, "banner.background")) {
      setSelectedMedia(_.get(stream, "banner.background"));
      setCropDetails(_.get(stream, "banner.crop"));
      setPosition(_.get(stream, "banner.position"));
    }
  }, [stream])

  useEffect(() => {
    if (updated) {
      setIsEditStream(false);
    }
  }, [updating])

  useEffect(() => {
    if (uploaded) {

      dispatch(updateStreamProperty({
        id: stream.id,
        property: 'banner',
        value: {
          ...stream.banner,
          background: uploadedBackground,
          crop: updatedThumbnail?.crop
        }
      }));

      const request = {
        ...stream,
        id: stream.id,
        summary: content,
        banner: updatedThumbnail === null ? {} : {
          autoHeightEnabled: false,
          background: uploadedBackground.id,
          filter: null,
          sharedByEnabled: false,
          position: position,
          overlayColor: null,
          fontColor: {
            r: 0, g: 1, b: 0, a: 0
          },
          bannerHeight: null,
          descriptionOverlay: null,
          crop: updatedThumbnail ? {
            x: updatedThumbnail?.crop?.x,
            y: updatedThumbnail?.crop?.y,
            width: updatedThumbnail?.crop?.width,
            height: updatedThumbnail?.crop?.height
          } : null
        }
      }

      updateStreamRequest({ request }).then((response) => {
        trackStreamEdited({stream: response});
      });
    }
  }, [uploaded])

  useEffect(() => {
    if (deleted) {
      setOpen(false);
    }
  }, [deleting])

  useEffect(() => {
    if (updated) {
      setUpdatedThumbnail(null);
    }
  }, [updated]);

  const toggleWidth = () => {
    setIsExpanded(!isExpanded);
    handleExpandContainer(isExpanded);
  };

  const editStreamClick = () => {
    setIsEditStream(true);
    trackEditStreamClicked({streamId: stream.id});
    trackStreamEditViewed();
  }

  const deleteStreamClick = (id: string) => {
    deleteStreamRequest({id, event: trackStreamDeleted})
      .finally(() => {
      navigate("/hub");
      })
    trackDeleteStreamConfirmClicked({streamId: stream.id});
  }

  const handleChange = (e: any) => {
    const content = e.target.getContent();
    setContent(content);
  }

  const editBannerClicked = () => {
    setIsEditBanner(true);
    trackChangeBackgroundClicked({streamId: stream.id});
    dispatch(updateStreamProperty({
      id: stream.id,
      property: 'banner',
      value: {
        ...stream.banner,
        background: selectedMedia
      }
    }))
  }

  const handleClose = (updatedThumbnail: any, position: string) => {
    setIsEditBanner(false);

    if (!_.isEmpty(updatedThumbnail)) {
      if (
        updatedThumbnail.background.id !== _.get(stream, "banner.background.id")
      ) {
        setUpdatedThumbnail(updatedThumbnail);
      }
      setCropDetails(updatedThumbnail?.crop);
      setPosition(position);
    }
  };

  const onSave = async () => {

    if (updatedThumbnail) {
      const banner = updatedThumbnail;
      const media = _.get(banner, 'background');

      const mediaFile = media as MediaFile;

      const blob = await fetch(media!.full!).then(r => r.blob());
      const file = new File([blob], 'banner.png', { type: mediaFile.contentType || blob.type });

      dispatch(uploadMedia({
        id: media!.id,
        media: {
          ...media,
          file: file
        } as MediaFile
      }));
    } else if(!updatedThumbnail){
      const request = {
        ...stream,
        id: stream.id,
        summary: content,
        banner: {
          ...stream.banner,
          background: stream.banner.id,
          crop: cropDetails,
          position: position
        }
      }

      updateStreamRequest({ request }).then((response) => {
        trackStreamEdited({stream: response});
      });
    }

    trackEditStreamConfirmClicked({streamName: stream.name, streamDescription: content});
  }

  const handleCancelClick = () => {
    setIsEditStream(false);
    setContent("");
    setUpdatedThumbnail(null);
    setSelectedMedia(_.get(stream, "icon"));
  }

  return (
    <div
      className={cx(
        styles.container,
        styles[isExpanded ? "expanded" : "collapsed"],
      )}
    >
      {loadingStream && (
        <div
          className={cx(styles.entity, styles.placeholder)}
          style={{ width: "100%" }}
        >
          <div className={cx(styles.placeholderContent, styles.block)}></div>
        </div>
      )}

      <div className={cx(styles.thumbnail)}>
        <Thumbnail
          media={updatedThumbnail?.background || selectedMedia}
          crop={updatedThumbnail?.crop || cropDetails}
          className={styles.backgroundImage}
          position={position}
          alt=""
          defaultImage="/images/assets/default_asset.jpg"
        />

        <div className={styles.overlay}></div>

        <div
          className={cx(styles.header, {
            [styles.hide]: !isExpanded,
          })}
        >
          {!isEditStream && !isEditBanner && _.get(stream, "canEdit") && (
            <>
              <Tooltip content="Edit Stream" placement="bottom">
                <span>
                  <Button
                    variant="iconButton"
                    icon="paperflite-edit"
                    onClick={editStreamClick}
                  />
                </span>
              </Tooltip>

              {_.get(stream, "canDelete") && (
                <Tooltip content="Delete Stream" placement="bottom">
                  <span>
                    <Button
                      variant="iconButton"
                      icon="paperflite-delete"
                      onClick={() => {
                        setOpen(true);
                        trackDeleteStreamClicked({ streamId: stream.id });
                      }}
                    />
                  </span>
                </Tooltip>
              )}
            </>
          )}

          {isEditStream && !isEditBanner && (
            <div
              onClick={editBannerClicked}
              className={styles.editBackGroundContainer}
            >
              <span>
                <i className="paperflite-edit"></i>
              </span>
              <span>Edit Background</span>
            </div>
          )}
        </div>

        <DeleteStream
          open={open}
          setOpen={setOpen}
          stream={stream}
          deleteStreamClick={deleteStreamClick}
        />

        <EditBanner
          stream={stream}
          isEditBanner={isEditBanner}
          onClose={handleClose}
        />

        {!isEditStream && !isEditBanner && (
          <div
            className={cx(styles.analytics, {
              [styles.hide]: !isExpanded,
            })}
          >
            <AnalyticsCard
              value={_.get(description, "totalViews", 0)}
              name="Views"
            />
            <AnalyticsCard
              value={_.get(description, "totalDownloads", 0)}
              name="Downloads"
            />
          </div>
        )}

        <div
          className={cx(styles.description, {
            [styles.hide]: !isExpanded,
            [styles.edit]: isEditStream,
          })}
        >
          <ReadMoreHtml className={styles.content}>
            <div
              dangerouslySetInnerHTML={{ __html: _.get(stream, "summary", "") }}
            />
          </ReadMoreHtml>

          {isEditStream && (
            <span className={styles.editContent}>
              <RichTextEditor
                content={
                  description?.description || _.get(stream, "summary", "")
                }
                onChange={handleChange}
                placeholder="Type here to add description"
                bodyColor="#FFFFFF"
                height={300}
              />
            </span>
          )}
        </div>

        {isEditStream && (
          <div
            className={cx(styles.actions, {
              [styles.hide]: !isExpanded,
            })}
          >
            <Button
              variant="primary"
              icon="paperflite-tick"
              text={updating || uploading ? "Saving..." : "Save"}
              disabled={updating || uploading}
              onClick={onSave}
            />

            <Button
              className={styles.cancelButton}
              variant="secondary"
              text="Cancel"
              disabled={updating}
              onClick={handleCancelClick}
            />
          </div>
        )}

        {!isEditStream && !isEditBanner && (
          <i
            className={cx(styles.collapse, "paperflite-dropdown", {
              [styles.collapsed]: !isExpanded,
            })}
            onClick={toggleWidth}
          ></i>
        )}
      </div>
    </div>
  );
};

export default StreamDescriptionView;